import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LifeBuoy, MessageCircle, Send, Loader2, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle, BookOpen, FolderOpen, Music, Cloud, Sparkles, Bug, HelpCircle, CreditCard, Lightbulb, BarChart3,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';

const FAQ = [
  {
    icon: Music,
    q: 'What is AI DJ software and how does CRATELAB work?',
    a: 'CRATELAB is an AI-powered DJ software that automatically analyzes your music tracks for BPM (beats per minute), musical key using the Camelot wheel system, energy levels, and song structure. Our intelligent algorithms help you create perfectly harmonic mixes by suggesting compatible tracks based on key compatibility and energy flow — no manual analysis needed.',
  },
  {
    icon: Sparkles,
    q: 'How does harmonic mixing with the Camelot wheel work?',
    a: 'Harmonic mixing uses the Camelot wheel to match tracks by their musical keys. CRATELAB automatically detects each track\'s key and assigns a Camelot code (like 8A or 9B). You can mix tracks with the same code, move +1/-1 step on the wheel, or switch between A (minor) and B (major) modes for seamless, professional-sounding DJ transitions.',
  },
  {
    icon: Music,
    q: 'How do I import and analyze tracks?',
    a: 'Go to the Upload page, drop in your MP3 files, and hit Upload. CRATELAB automatically analyzes BPM, Camelot key, energy, and structure for every track server-side — no software to install. Your tracks appear in your Library within seconds.',
  },
  {
    icon: Cloud,
    q: 'Is this cloud-based DJ software? Where are my files stored?',
    a: 'Yes, CRATELAB is fully cloud-based. Your audio files are uploaded to Cloudflare R2 object storage and streamed securely back to you in the browser. Metadata and analysis results are stored in MongoDB. Everything is accessible from any device with a browser — no desktop installation required.',
  },
  {
    icon: Sparkles,
    q: 'How do I generate an AI-powered DJ playlist?',
    a: 'Open the Playlists screen → New Playlist → AI. Pick a seed track, choose your energy curve (build, peak, cool), and target length. CRATELAB\'s AI builds a harmonically-matched, energy-shaped set in seconds using BPM matching and Camelot wheel compatibility. You can drag tracks to fine-tune the AI-generated playlist.',
  },
  {
    icon: BarChart3,
    q: 'What DJ features does CRATELAB include?',
    a: 'CRATELAB includes automatic BPM detection, Camelot key analysis for harmonic mixing, energy scoring (0-100), track structure mapping (intro, build, drop, outro), AI playlist generation, cloud sync across devices, crate organization with color coding, transition compatibility scoring, and an AI DJ coach assistant.',
  },
  {
    icon: Lightbulb,
    q: 'Can I use CRATELAB for live DJ sets or just playlist preparation?',
    a: 'CRATELAB is designed as a professional DJ preparation and analysis tool. Use it to analyze your library, prepare harmonically compatible sets, organize your crates, and build playlists with perfect energy flow. The AI assistant helps you plan transitions and discover compatible tracks before your live performance.',
  },
  {
    icon: FolderOpen,
    q: 'My audio cuts out when my phone locks. Help?',
    a: 'CRATELAB uses MediaSession + a heartbeat to keep audio alive on iOS lock-screen. If audio still drops after a deploy, fully reload the tab once — a service-worker update toast will appear after the next deploy.',
  },
  {
    icon: BookOpen,
    q: 'Where can I read the full docs?',
    a: 'Visit the Docs page from the top nav or go to /docs. It covers uploading, playlists, and the Camelot wheel.',
  },
];

const CATEGORIES = [
  { value: 'bug', label: 'Bug', icon: Bug, color: 'text-[#2563FF] border-[#2563FF]/40' },
  { value: 'question', label: 'Question', icon: HelpCircle, color: 'text-blue-300 border-blue-500/40' },
  { value: 'feature', label: 'Feature request', icon: Lightbulb, color: 'text-yellow-300 border-yellow-500/40' },
  { value: 'billing', label: 'Billing', icon: CreditCard, color: 'text-green-300 border-green-500/40' },
  { value: 'other', label: 'Other', icon: HelpCircle, color: 'text-[#334155] border-gray-500/40' },
];

function FaqItem({ icon: Icon, q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E2E8F0] bg-white" data-testid={`faq-item-${idx}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F1F5F9] transition-colors"
        data-testid={`faq-toggle-${idx}`}
      >
        <Icon className="w-4 h-4 text-[#2563FF] shrink-0" />
        <span className="flex-1 font-body text-sm text-[#0F172A]">{q}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-[#64748B]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#64748B]" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 text-sm text-[#64748B] leading-relaxed font-body" data-testid={`faq-body-${idx}`}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'ticket' ? 'ticket' : 'faq';
  const [tab, setTab] = useState(initialTab);
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'question',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // ticket id

  // Pre-fill from auth
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || '',
        email: f.email || user.email || '',
      }));
    }
  }, [user]);

  // Pre-fill subject from query param
  useEffect(() => {
    const sub = searchParams.get('subject');
    if (sub) setForm((f) => ({ ...f, subject: sub }));
  }, [searchParams]);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill out all fields');
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error('Please describe the issue in a bit more detail (min 10 chars)');
      return;
    }
    setSubmitting(true);
    try {
      const chatSession =
        localStorage.getItem('cratelab_support_session') || undefined;
      const { data } = await axios.post(`${API}/support/tickets`, {
        ...form,
        email: form.email.trim().toLowerCase(),
        chat_session_id: chatSession,
      });
      setSubmitted(data.id);
      toast.success('Ticket submitted — we\'ll reply by email');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#0F172A]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 border border-[#E2E8F0] bg-[#F1F5F9]">
            <LifeBuoy className="w-3.5 h-3.5 text-[#2563FF]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#2563FF]">
              Support
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#0F172A] tracking-tight mb-3" data-testid="support-page-title">
            How can we help?
          </h1>
          <p className="font-body text-[#64748B] max-w-2xl">
            Browse common questions, chat with our AI assistant, or open a ticket
            and our team will reply by email.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#E2E8F0]">
          <button
            onClick={() => setTab('faq')}
            data-testid="support-tab-faq"
            className={`px-4 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors ${
              tab === 'faq'
                ? 'text-[#2563FF] border-b-2 border-[#2563FF]'
                : 'text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            FAQ
          </button>
          <button
            onClick={() => setTab('ticket')}
            data-testid="support-tab-ticket"
            className={`px-4 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors ${
              tab === 'ticket'
                ? 'text-[#2563FF] border-b-2 border-[#2563FF]'
                : 'text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            <Send className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Open a ticket
          </button>
        </div>

        {tab === 'faq' && (
          <div className="grid gap-3" data-testid="support-faq-panel">
            {FAQ.map((item, i) => (
              <FaqItem key={i} idx={i} {...item} />
            ))}
            <div className="mt-6 p-5 rounded-xl border border-[#E2E8F0] bg-gradient-to-br from-[#EFF6FF] to-white">
              <div className="flex items-start gap-3">
                <MessageCircle className="w-5 h-5 text-[#2563FF] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-heading text-sm text-[#0F172A] mb-1">
                    Didn't find your answer?
                  </p>
                  <p className="font-body text-sm text-[#64748B] mb-3">
                    Use the chat bubble in the bottom-right for instant AI help, or
                    open a ticket and our team will get back to you by email.
                  </p>
                  <button
                    onClick={() => setTab('ticket')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A] text-xs font-heading uppercase tracking-widest transition-colors"
                    data-testid="support-cta-open-ticket"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Open a ticket
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'ticket' && (
          <div data-testid="support-ticket-panel">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="border border-green-700/40 bg-green-950/20 p-8 text-center"
                data-testid="support-ticket-success"
              >
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h2 className="font-heading text-xl text-[#0F172A] mb-2">
                  Ticket received
                </h2>
                <p className="font-body text-sm text-[#64748B] mb-1">
                  We've sent it to the team. You'll get a reply at:
                </p>
                <p className="font-mono text-sm text-[#0F172A] mb-4">{form.email}</p>
                <p className="font-mono text-[11px] text-[#94A3B8] mb-6">
                  Reference ID: {submitted}
                </p>
                <button
                  onClick={() => {
                    setSubmitted(null);
                    setForm({
                      name: user?.name || '',
                      email: user?.email || '',
                      subject: '',
                      category: 'question',
                      message: '',
                    });
                  }}
                  className="px-4 py-2 border border-[#E2E8F0] hover:border-[#2563FF]/50 text-[#334155] hover:text-[#0F172A] text-xs font-heading uppercase tracking-widest"
                  data-testid="support-submit-another"
                >
                  Submit another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-5 max-w-2xl" data-testid="support-ticket-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
                      Your name
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={onChange('name')}
                      className="w-full bg-white border border-[#E2E8F0] focus:border-[#2563FF]/60 outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder-gray-600"
                      placeholder="Sarah K."
                      data-testid="support-input-name"
                      maxLength={120}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChange('email')}
                      className="w-full bg-white border border-[#E2E8F0] focus:border-[#2563FF]/60 outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder-gray-600"
                      placeholder="you@example.com"
                      data-testid="support-input-email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
                    Category
                  </label>
                  <div className="flex flex-wrap gap-2" data-testid="support-categories">
                    {CATEGORIES.map((c) => {
                      const active = form.category === c.value;
                      return (
                        <button
                          type="button"
                          key={c.value}
                          onClick={() => setForm((f) => ({ ...f, category: c.value }))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono uppercase tracking-wider transition-colors ${
                            active
                              ? `bg-[#EFF6FF] ${c.color} border-current`
                              : 'border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A] hover:border-[#CBD5E1]'
                          }`}
                          data-testid={`support-category-${c.value}`}
                        >
                          <c.icon className="w-3.5 h-3.5" />
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={onChange('subject')}
                    className="w-full bg-white border border-[#E2E8F0] focus:border-[#2563FF]/60 outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder-gray-600"
                    placeholder="Sync stuck at 50%"
                    data-testid="support-input-subject"
                    maxLength={200}
                  />
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
                    Message
                  </label>
                  <textarea
                    value={form.message}
                    onChange={onChange('message')}
                    rows={6}
                    className="w-full bg-white border border-[#E2E8F0] focus:border-[#2563FF]/60 outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder-gray-600 resize-none"
                    placeholder="Tell us what's happening — include steps to reproduce, what you expected, and any error messages."
                    data-testid="support-input-message"
                    maxLength={5000}
                  />
                  <p className="font-mono text-[10px] text-[#475569] mt-1">
                    {form.message.length} / 5000
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2563FF] hover:bg-[#2563FF] disabled:bg-[#EFF6FF] disabled:text-[#64748B] text-[#0F172A] text-xs font-heading uppercase tracking-widest transition-colors"
                    data-testid="support-submit-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    {submitting ? 'Sending…' : 'Submit ticket'}
                  </button>
                  <Link
                    to="/docs"
                    className="font-mono text-[11px] text-[#64748B] hover:text-[#0F172A] uppercase tracking-wider"
                  >
                    Read the docs first →
                  </Link>
                </div>

                {!user && (
                  <div className="flex items-start gap-2 p-3 border border-yellow-700/30 bg-yellow-950/10 text-xs text-yellow-200/80">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      Submitting as a guest. <Link to="/login" className="underline text-yellow-300">Log in</Link> first to link this ticket to your account.
                    </span>
                  </div>
                )}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
